import React from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Paper,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { WEIGHT_PRESETS, weightSum } from './indicatorConfig';
import { formatPercent } from './formatters';
import WeightPanel from './WeightPanel';
import BreakpointEditor from './BreakpointEditor';

const ControlLabel = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      minWidth: 104,
      fontSize: '0.65rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      color: 'text.secondary',
      flexShrink: 0,
    }}
  >
    {children}
  </Typography>
);

const ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  flexWrap: 'wrap',
  px: 2,
  py: 1.25,
};

const TOGGLE_SX = { py: 0.25, px: 1.25, fontSize: '0.7rem', lineHeight: 1.6, textTransform: 'none' };

const ScoringModelBar = ({
  open,
  onToggle,
  indicators,
  weights,
  onWeightChange,
  onPreset,
  presetId,
  minEnrollment,
  onMinEnrollment,
  autoNormalize,
  onAutoNormalize,
  renormalize,
  onRenormalize,
  breakpointsById,
  onBreakpointChange,
  onBreakpointReset,
  entityCount,
  totalCount,
}) => {
  const sum = weightSum(weights);
  const sumOk = Math.abs(sum - 1) < 0.005;
  const presetLabel =
    WEIGHT_PRESETS.find((preset) => preset.id === presetId)?.label || 'Custom weights';

  const summaryChips = [
    presetLabel,
    `Min enrollment ${Number(minEnrollment).toLocaleString()}`,
    autoNormalize ? 'Auto-normalized' : `Raw weights (${formatPercent(sum, 0)})`,
    renormalize ? 'Renormalized over available' : 'Missing scored as zero',
  ];

  return (
    <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <TuneIcon fontSize="small" color="action" />
        <Typography variant="subtitle2" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
          Scoring model
        </Typography>
        <Box
          data-testid="scoring-model-summary"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            flexWrap: 'wrap',
            flex: 1,
            minWidth: 0,
          }}
        >
          {!open &&
            summaryChips.map((label) => (
              <Chip
                key={label}
                label={label}
                size="small"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.7rem' }}
              />
            ))}
          {!open && !sumOk && !autoNormalize && (
            <Chip
              label="Does not sum to 100%"
              size="small"
              color="warning"
              sx={{ height: 22, fontSize: '0.7rem' }}
            />
          )}
          {open && (
            <Chip
              label={`${entityCount} of ${totalCount} scored`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: 22, fontSize: '0.7rem' }}
            />
          )}
        </Box>
        <IconButton size="small" aria-label={open ? 'Collapse scoring model' : 'Expand scoring model'}>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={open} unmountOnExit>
        <Divider />

        <Box sx={ROW_SX}>
          <ControlLabel>Filters</ControlLabel>
          <TextField
            size="small"
            type="number"
            label="Minimum enrollment"
            value={minEnrollment}
            onChange={(event) => {
              const next = Number(event.target.value);
              onMinEnrollment(Number.isFinite(next) && next >= 0 ? next : 0);
            }}
            inputProps={{ min: 0, step: 100 }}
            sx={{ width: 190 }}
          />
          <Typography variant="caption" color="text.secondary">
            Workbook default is 1,000. Set to 0 to include every entity.
          </Typography>
        </Box>

        <Divider />

        <Box sx={ROW_SX}>
          <ControlLabel>Scoring</ControlLabel>
          <Tooltip title="Auto-normalize rescales the slider mix to sum to 100% before scoring. Raw uses the sliders as-is, so totals shrink or inflate when they do not add to 100%.">
            <ToggleButtonGroup
              exclusive
              size="small"
              value={autoNormalize ? 'auto' : 'raw'}
              onChange={(_, value) => {
                if (value !== null) onAutoNormalize(value === 'auto');
              }}
            >
              <ToggleButton value="auto" sx={TOGGLE_SX}>
                Auto-normalize
              </ToggleButton>
              <ToggleButton value="raw" sx={TOGGLE_SX}>
                Raw weights
              </ToggleButton>
            </ToggleButtonGroup>
          </Tooltip>
          <Tooltip title="Raw scores missing indicators as zero, matching the spreadsheet. Renormalized redistributes their weight across the indicators that do have data, so totals stay comparable.">
            <ToggleButtonGroup
              exclusive
              size="small"
              value={renormalize ? 'renormalized' : 'raw'}
              onChange={(_, value) => {
                if (value !== null) onRenormalize(value === 'renormalized');
              }}
            >
              <ToggleButton value="raw" sx={TOGGLE_SX}>
                Missing = 0
              </ToggleButton>
              <ToggleButton value="renormalized" sx={TOGGLE_SX}>
                Renormalized
              </ToggleButton>
            </ToggleButtonGroup>
          </Tooltip>
          <Chip
            size="small"
            color={sumOk ? 'success' : 'warning'}
            variant={sumOk ? 'outlined' : 'filled'}
            label={`Weights sum ${formatPercent(sum, 0)}`}
            sx={{ height: 24 }}
          />
        </Box>

        <Divider />

        <Box sx={ROW_SX}>
          <ControlLabel>Presets</ControlLabel>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {WEIGHT_PRESETS.map((preset) => (
              <Button
                key={preset.id}
                size="small"
                variant={presetId === preset.id ? 'contained' : 'outlined'}
                onClick={() => onPreset(preset.id)}
                sx={{ textTransform: 'none' }}
              >
                {preset.label}
              </Button>
            ))}
          </Stack>
        </Box>

        <Divider />

        <Box sx={{ px: 2, py: 1.75 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Breakpoints still use the workbook&apos;s Score 1&ndash;5 bands. Displayed indicator
            scores and combined opportunity scores are on a 100-point scale.
          </Typography>
          <WeightPanel indicators={indicators} weights={weights} onChange={onWeightChange} />
        </Box>

        <Divider />

        <BreakpointEditor
          indicators={indicators}
          breakpointsById={breakpointsById}
          onChange={onBreakpointChange}
          onReset={onBreakpointReset}
        />
      </Collapse>
    </Paper>
  );
};

export default ScoringModelBar;
